import type { AxiosRequestConfig } from "axios";
import { apiAgent } from "@/network/axiosAgent";
import { downloadFileFromData } from "@/other/utils/utils";
import {
  checklistDetailFromRetrieve,
  checklistFromBoardListRow,
  type BoardChecklistListRow,
  type ChecklistDetail,
  type ChecklistRetrievePayload
} from "../utils/checklistApiAdapters";
import type {
  ChecklistItemImageUploadResponse,
  ChecklistRecordWriteResponse,
  ChecklistWriteResponse,
  CreateChecklistItemPayload,
  CreateChecklistPayload,
  DuplicateChecklistPayload,
  EntityChecklistsPage,
  ChecklistItemStatusLogRow,
  FetchChecklistDetailParams,
  FetchChecklistItemStatusLogParams,
  FetchChecklistStatusMatrixParams,
  FetchEntityChecklistsParams,
  PatchChecklistItemPayload,
  PatchChecklistPayload
} from "./checklist.types";

/** Custom flag read by axiosAgent 403/404 interceptor (not on Axios typings). */
const quiet = { skipAccessDeniedToast: true } as AxiosRequestConfig;

function checklistBase(boardId: number): string {
  return `board/${boardId}/checklists`;
}

type BoardChecklistListApiPage = {
  count?: number;
  results?: BoardChecklistListRow[];
};

/**
 * GET /board/<board_id>/checklists/list
 *
 * - With entityType + entityId: filtered entity Manager list (paginated).
 * - Without: board-wide list for Checklists view (paginated; optional `archive`).
 */
export async function fetchEntityChecklists({
  boardId,
  entityType,
  entityId,
  limit,
  offset,
  archive,
  progress,
  assignedTo,
  filterEntityType,
  createdWithinDays,
  search,
  recurrence
}: FetchEntityChecklistsParams): Promise<EntityChecklistsPage> {
  const params: Record<string, unknown> = {};
  if (limit != null) params.limit = limit;
  if (offset != null) params.offset = offset;
  if (entityType != null && entityId != null && entityId !== "") {
    params[entityType] = entityId;
  }
  if (archive != null) params.archive = archive;
  if (progress?.length) params.progress = progress.join(",");
  if (assignedTo != null) params.assigned_to = assignedTo;
  if (filterEntityType != null) params.entity_type = filterEntityType;
  if (createdWithinDays != null) params.created_within_days = createdWithinDays;
  if (search) params.search = search;
  if (recurrence) params.recurrence = recurrence;

  const { data } = await apiAgent.get(`board/${boardId}/checklists/list`, {
    params,
    ...quiet
  });

  // Board-wide responses may be a bare array; entity list is usually `{ count, results }`.
  if (Array.isArray(data)) {
    const rows = data as BoardChecklistListRow[];
    return {
      count: rows.length,
      results: rows.map((row) => checklistFromBoardListRow(row))
    };
  }

  const page = (data ?? {}) as BoardChecklistListApiPage;
  const rows = Array.isArray(page.results) ? page.results : [];
  const parsedCount = Number(page.count);
  return {
    count: Number.isFinite(parsedCount) && parsedCount >= 0 ? parsedCount : rows.length,
    results: rows.map((row) =>
      checklistFromBoardListRow(row, {
        entityType: entityType ?? undefined,
        entityId: entityId ?? undefined
      })
    )
  };
}

/** GET /board/<board_id>/checklists/<checklist_id> */
export async function fetchChecklistDetail({
  boardId,
  checklistId,
  ...mapOptions
}: FetchChecklistDetailParams): Promise<ChecklistDetail> {
  const { data } = await apiAgent.get(`${checklistBase(boardId)}/${checklistId}`, quiet);
  if (data?.id == null) {
    throw new Error("Unable to load this checklist.");
  }
  return checklistDetailFromRetrieve(data as ChecklistRetrievePayload, mapOptions);
}

/** GET /board/<board_id>/checklists/<id>/status-matrix/ */
export async function fetchChecklistStatusMatrix({
  boardId,
  checklistId,
  to
}: FetchChecklistStatusMatrixParams): Promise<unknown> {
  const { data } = await apiAgent.get(`${checklistBase(boardId)}/${checklistId}/status-matrix/`, {
    params: to != null ? { to } : undefined,
    ...quiet
  });
  return data;
}

/** GET /board/<board_id>/checklists/item/<itemId>/status-log/ */
export async function fetchChecklistItemStatusLog({
  boardId,
  itemId,
  boardUserId
}: FetchChecklistItemStatusLogParams): Promise<ChecklistItemStatusLogRow[]> {
  const { data } = await apiAgent.get(`${checklistBase(boardId)}/item/${itemId}/status-log/`, {
    params: boardUserId != null ? { board_user_id: boardUserId } : undefined,
    ...quiet
  });
  if (Array.isArray(data)) return data as ChecklistItemStatusLogRow[];
  const page = data as { results?: ChecklistItemStatusLogRow[] } | null;
  return Array.isArray(page?.results) ? page.results : [];
}

export async function createChecklist(
  boardId: number,
  body: CreateChecklistPayload
): Promise<ChecklistRecordWriteResponse> {
  const { data } = await apiAgent.post(`${checklistBase(boardId)}/`, body, quiet);
  return data as ChecklistRecordWriteResponse;
}

export async function patchChecklist(
  boardId: number,
  checklistId: number | string,
  body: PatchChecklistPayload
): Promise<ChecklistRecordWriteResponse> {
  const { data } = await apiAgent.patch(`${checklistBase(boardId)}/${checklistId}`, body, quiet);
  return data as ChecklistRecordWriteResponse;
}

export async function deleteChecklist(boardId: number, checklistId: number | string): Promise<void> {
  await apiAgent.delete(`${checklistBase(boardId)}/${checklistId}`, quiet);
}

export async function createChecklistItem(
  boardId: number,
  checklistId: number | string,
  body: CreateChecklistItemPayload
): Promise<ChecklistWriteResponse> {
  const { data } = await apiAgent.post(`${checklistBase(boardId)}/${checklistId}/items`, body, quiet);
  return data as ChecklistWriteResponse;
}

export async function patchChecklistItem(
  boardId: number,
  itemId: number | string,
  body: PatchChecklistItemPayload
): Promise<ChecklistWriteResponse> {
  const { data } = await apiAgent.patch(`${checklistBase(boardId)}/item/${itemId}`, body, quiet);
  return data as ChecklistWriteResponse;
}

export async function deleteChecklistItem(boardId: number, itemId: number | string): Promise<void> {
  await apiAgent.delete(`${checklistBase(boardId)}/item/${itemId}`, quiet);
}

export async function duplicateChecklist(
  checklistId: number | string,
  body: DuplicateChecklistPayload
): Promise<ChecklistWriteResponse> {
  const { data } = await apiAgent.put(`/checklist/${checklistId}/duplicate`, body, quiet);
  return data as ChecklistWriteResponse;
}

export async function importChecklistItems(
  checklistId: number | string,
  checklistItemsData: string
): Promise<unknown> {
  const { data } = await apiAgent.post(
    `/checklist/${checklistId}/items/import`,
    { checklist_items_data: checklistItemsData },
    quiet
  );
  return data;
}

export async function exportChecklistItems(
  checklistId: number | string,
  fileName = "ChecklistItems"
): Promise<void> {
  const { data } = await apiAgent.get(`/checklist/${checklistId}/items/export`, {
    responseType: "blob",
    ...quiet
  });
  if (data) downloadFileFromData(data, fileName);
}

export async function uploadChecklistItemImage(
  boardId: number,
  itemId: number | string,
  file: File
): Promise<ChecklistItemImageUploadResponse> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await apiAgent.post(
    `/board/${boardId}/checklists/item/${itemId}/upload`,
    form,
    quiet
  );
  return data as ChecklistItemImageUploadResponse;
}

export async function removeChecklistItemImage(itemId: number | string): Promise<void> {
  await apiAgent.patch(`/checklist/item/${itemId}`, { remove_file: true }, quiet);
}

/** POST /checklist/<id>/nudge */
export async function sendChecklistNudge(checklistId: number | string): Promise<void> {
  await apiAgent.post(`/checklist/${checklistId}/nudge`, undefined, quiet);
}
